/**
 * Binding to the Rust chunk rule (`fluxability_planning_runtime::split_policy::preview`) for the
 * editor's splitting hint. It loads the same-origin canonical planning WASM on first use and
 * moves numbers in and JSON out; every decision is made in Rust. A failed load resolves to null so
 * the editor can show a plain hint instead.
 *
 * @module apps/web/src/lib/splitPolicyPreview
 */

/** Build-injected hash of the canonical planning artifacts; undefined in direct test harnesses. */
declare const __SCHEDULING_RUNTIME_VERSION__: string | undefined;

/** Chunk preview returned by Rust for one activity and percentage. */
export interface SplitPreview {
  /** Shortest allowed chunk in minutes. */
  min_chunk_minutes: number;
  /** Most chunks the percentage allows. */
  max_chunks: number;
  /** Chunk lengths in minutes for the remaining work; one entry means it stays whole. */
  chunks: number[];
}

/** Result of one preview call: a preview, or the Rust validation message. */
export type SplitPreviewResult =
  | {
      /** Successful preview. */ ok: true;
      /** Preview computed by Rust. */ preview: SplitPreview;
    }
  | {
      /** Rejected input. */ ok: false;
      /** Validation message from Rust. */ error: string;
    };

/** Subset of the generated canonical module this binding calls. */
interface PlanningModule {
  /** Initialize the generated WASM glue from a same-origin URL. */
  default: (options: {
    /** Same-origin URL of the canonical WASM artifact. */ module_or_path: string;
  }) => Promise<unknown>;
  /** Rust export; throws a string for an out-of-range percentage. */
  split_policy_preview?: (total: number, remaining: number, percent: number) => string;
}

/** Cached module load; reset after a failure so a later call can retry. */
let loading: Promise<PlanningModule | null> | undefined;

/** Load the canonical planning module once, resolving to null when it is unavailable. */
function loadPlanningModule(): Promise<PlanningModule | null> {
  if (!loading) {
    const version =
      typeof __SCHEDULING_RUNTIME_VERSION__ === 'string'
        ? `?v=${encodeURIComponent(__SCHEDULING_RUNTIME_VERSION__)}`
        : '';
    loading = (async () => {
      const origin = window.location.origin;
      const url = new URL(`/planning/fluxability_planning_runtime.js${version}`, origin).href;
      const module: PlanningModule = await import(/* @vite-ignore */ url);
      await module.default({
        module_or_path: new URL(`/planning/fluxability_planning_runtime_bg.wasm${version}`, origin)
          .href,
      });
      return typeof module.split_policy_preview === 'function' ? module : null;
    })().catch(() => {
      loading = undefined;
      return null;
    });
  }
  return loading;
}

/**
 * Ask Rust how `remaining` of `total` minutes would be chunked at `percent`. Resolves to null
 * when the planning module cannot be loaded.
 */
export async function previewSplitPolicy(
  total: number,
  remaining: number,
  percent: number
): Promise<SplitPreviewResult | null> {
  const module = await loadPlanningModule();
  if (!module?.split_policy_preview) return null;
  // WASM takes u32 arguments; pass whole, nonnegative minutes and let Rust judge the percentage.
  const minutes = (value: number) => (Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0);
  try {
    return {
      ok: true,
      preview: JSON.parse(
        module.split_policy_preview(minutes(total), minutes(remaining), Math.trunc(percent))
      ),
    };
  } catch (error) {
    return { ok: false, error: typeof error === 'string' ? error : String(error) };
  }
}
